import createClient from 'openapi-fetch';
import type { paths } from './api-types'; // generated by openapi-typescript
import { getAccessToken, refresh } from '../services/tokenManager';

// Base URL can be configured via env; falls back to localhost in development
const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002';

const client = createClient<paths>({
  baseUrl,
});

// Attach Authorization header from memory for every request
// Handle 401 responses by refreshing token and retrying
client.use({
  async onRequest({ request }) {
    const token = getAccessToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    } else {
      // Ensure no stale auth header remains
      request.headers.delete('Authorization');
    }
    // Use mobile client flow for all requests
    request.headers.set('X-Client-Type', 'mobile');
    return request;
  },

  async onResponse({ response, request }) {
    // The backend returns 403 with code INVITATION_NOT_ACCEPTED when an
    // authenticated user hits an org endpoint for an org they haven't accepted
    // the invitation to. Surface it as a window event so the app can route the
    // user to the accept-invitation screen. We clone() so reading the body here
    // doesn't consume the stream the caller still needs.
    if (response.status === 403) {
      try {
        const body = await response.clone().json();
        if (body?.code === 'INVITATION_NOT_ACCEPTED' && body?.organization_id) {
          window.dispatchEvent(
            new CustomEvent('app:invitation-not-accepted', {
              detail: { organizationId: Number(body.organization_id) },
            })
          );
        }
      } catch {
        // Non-JSON / unexpected body — ignore, fall through to return response.
      }
      return response;
    }

    // Handle 401 by refreshing token and retrying (once)
    if (response.status === 401 && !request.headers.get('X-Retry')) {
      try {
        // Attempt to refresh the token
        await refresh();
        const newToken = getAccessToken();

        if (newToken) {
          // Clone the original request with new token and retry flag
          const retryHeaders = new Headers(request.headers);
          retryHeaders.set('Authorization', `Bearer ${newToken}`);
          retryHeaders.set('X-Retry', 'true');

          // Retry the request
          const retryResponse = await fetch(request.url, {
            method: request.method,
            headers: retryHeaders,
            body: request.body,
          });

          return retryResponse;
        }
      } catch {
        // Refresh failed - return original 401 response
        // The auth state will be cleared by tokenManager
      }
    }
    return response;
  },
});

export default client;
